// @file InteractTool for TLDraw
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import { useWhiteboardLinkingStore } from '@@/pinia/whiteboard_linking'
import { useWhiteboardScriptStore } from '@@/pinia/whiteboard_script'
import type { Editor, TLShape } from '@tldraw/editor'
import { defaultTldrawOptions } from '@tldraw/editor'
import type { TLEventHandlers } from 'tldraw'
import { StateNode, throttle } from 'tldraw'

// Copied from https://github.com/tldraw/tldraw/blob/main/packages/tldraw/src/lib/tools/SelectTool/childStates/Idle.ts
function getHitShapeOnCanvasPointerDown(editor: Editor, hitLabels = false): TLShape | undefined {
  const zoomLevel = editor.getZoomLevel()
  const {
    inputs: { currentPagePoint },
  } = editor

  return (
    // hovered shape at point
    editor.getShapeAtPoint(currentPagePoint, {
      hitInside: false,
      hitLabels,
      margin: defaultTldrawOptions.hitTestMargin / zoomLevel,
      renderingOnly: true,
    }) ??
    // selected shape at point
    editor.getSelectedShapeAtPoint(currentPagePoint)
  )
}

// Copied from https://github.com/tldraw/tldraw/blob/main/packages/tldraw/src/lib/tools/SelectTool/childStates/Idle.ts
function _updateHoveredShapeId(editor: Editor): Editor {
  const hitShape = editor.getShapeAtPoint(editor.inputs.currentPagePoint, {
    hitInside: false,
    hitLabels: false,
    margin: defaultTldrawOptions.hitTestMargin / editor.getZoomLevel(),
    renderingOnly: true,
  })

  if (hitShape == null) return editor.setHoveredShape(null)

  let shapeToHover: TLShape | undefined

  const outermostShape = editor.getOutermostSelectableShape(hitShape)

  if (outermostShape === hitShape) {
    shapeToHover = hitShape
  } else {
    if (outermostShape.id === editor.getFocusedGroupId() || editor.getSelectedShapeIds().includes(outermostShape.id)) {
      shapeToHover = hitShape
    } else {
      shapeToHover = outermostShape
    }
  }

  // Setting hovered shape here would help in showing the hover effect on the shape in ShapeIndicator.tsx
  return editor.setHoveredShape(shapeToHover.id)
}

// Copied from https://github.com/tldraw/tldraw/blob/main/packages/tldraw/src/lib/tools/SelectTool/childStates/Idle.ts
export const updateHoveredShapeId = throttle(_updateHoveredShapeId, 32)

class InteractTool extends StateNode {
  static override id = 'interact'
  whiteboardScriptStore: ReturnType<typeof useWhiteboardScriptStore> | null = null
  whiteboardLinkingStore: ReturnType<typeof useWhiteboardLinkingStore> | null = null
  whiteboardStore: ReturnType<typeof useWhiteboardStore> | null = null

  override onEnter = (): void => {
    // Clear selected shapes from SelectTool so that hover effect can be shown
    this.editor.setSelectedShapes([])
    updateHoveredShapeId(this.editor)
    this.editor.setCursor({ type: 'default', rotation: 0 })
    this.whiteboardScriptStore = useWhiteboardScriptStore() // TODO: check if we need to re-initalize this when a script is changed remotely with realtime
    this.whiteboardLinkingStore = useWhiteboardLinkingStore()
    this.whiteboardStore = useWhiteboardStore()
  }

  override onPointerMove: TLEventHandlers['onPointerMove'] = () => {
    updateHoveredShapeId(this.editor)

    const shapeId = this.editor.getHoveredShapeId()
    if (shapeId != null) {
      if (
        ((this.whiteboardScriptStore?.checkIfScriptExists(shapeId) ?? false) ||
          (this.whiteboardLinkingStore?.checkIfLinkingExists(shapeId) ?? false)) &&
        this.whiteboardStore?.isFrameBreakout === false // We don't want users to be able to navigate frames in breakout mode
      ) {
        this.editor.setCursor({ type: 'pointer', rotation: 0 })
      }
    } else {
      this.editor.setCursor({ type: 'default', rotation: 0 })
    }
  }

  // Copied from https://github.com/tldraw/tldraw/blob/main/packages/tldraw/src/lib/tools/SelectTool/childStates/Idle.ts
  override onPointerDown: TLEventHandlers['onPointerDown'] = (info): void => {
    switch (info.target) {
      case 'canvas': {
        // Check to see if we hit any shape under the pointer; if so,
        // handle this as a pointer down on the shape instead of the canvas
        const hitShape = getHitShapeOnCanvasPointerDown(this.editor)
        if (hitShape != null) {
          this.onPointerDown({
            ...info,
            shape: hitShape,
            target: 'shape',
          })
        }
        break
      }
      case 'shape': {
        if (info.shape != null) {
          // Since tldraw editor has a set of events that it emits, we ignore the error to emit our own event
          // @ts-expect-error Argument of type '"interact:shape"' is not assignable to parameter of type 'keyof TLEventMap'.ts(2345)
          this.editor.emit('interact:shape', { shapeId: info.shape.id, eventName: 'click' })
        }
        break
      }
    }
  }

  override onDoubleClick: TLEventHandlers['onDoubleClick'] = (): void => {
    // TODO: Implement double click
  }

  // TODO: Implement onLongPress, onMiddleClick, onTripleClick, onQuadrupleClick
}

export default InteractTool
